import Head from 'next/head';
import {useRouter} from 'next/router';

import {
	buildAbsolutePublicUrl,
	buildAlternateLinks,
	normalizeAppPath,
} from './urls';
import {serializeJsonLd} from './structuredData';

type SeoImage = {
	url: string;
	alt: string;
	width?: number;
	height?: number;
};

type RobotsContent = 'index,follow' | 'noindex,nofollow';

type SeoHeadProps = {
	title: string;
	description: string;
	path?: string;
	image?: SeoImage;
	jsonLd?: Array<Record<string, unknown>>;
	robots?: RobotsContent;
};

export const SeoHead = ({
	title,
	description,
	path,
	image,
	jsonLd = [],
	robots = 'noindex,nofollow',
}: SeoHeadProps) => {
	const router = useRouter();
	const appPath = normalizeAppPath(path ?? router.asPath);
	const locale = router.locale ?? router.defaultLocale;
	const canonicalUrl = buildAbsolutePublicUrl({locale, path: appPath});
	const alternateLinks = buildAlternateLinks(appPath);
	const isIndexable = robots === 'index,follow';

	return (
		<Head>
			<title>{title}</title>
			<meta name="description" content={description} />
			<meta name="robots" content={robots} />
			{isIndexable ? <link rel="canonical" href={canonicalUrl} /> : null}
			{isIndexable
				? alternateLinks.map(({hrefLang, href}) => (
						<link
							key={hrefLang}
							rel="alternate"
							hrefLang={hrefLang}
							href={href}
						/>
					))
				: null}
			<meta property="og:site_name" content="Deezer" />
			<meta property="og:type" content="website" />
			<meta property="og:title" content={title} />
			<meta property="og:description" content={description} />
			{isIndexable ? <meta property="og:url" content={canonicalUrl} /> : null}
			<meta
				name="twitter:card"
				content={image ? 'summary_large_image' : 'summary'}
			/>
			<meta name="twitter:title" content={title} />
			<meta name="twitter:description" content={description} />
			{/* Intentionally omit image fallbacks until the official share asset exists. */}
			{image ? (
				<>
					<meta property="og:image" content={image.url} />
					<meta property="og:image:alt" content={image.alt} />
					{image.width ? (
						<meta property="og:image:width" content={String(image.width)} />
					) : null}
					{image.height ? (
						<meta property="og:image:height" content={String(image.height)} />
					) : null}
					<meta name="twitter:image" content={image.url} />
				</>
			) : null}
			{jsonLd.map((data, index) => (
				<script
					key={index}
					type="application/ld+json"
					dangerouslySetInnerHTML={{__html: serializeJsonLd(data)}}
				/>
			))}
		</Head>
	);
};
